import React, { useEffect } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import {
  Shield,
  Lock,
  Eye,
  AlertTriangle,
  FileText,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Globe,
  Database,
  Network,
  Cpu,
  Binary,
  Compass,
  FileCheck2,
  ExternalLink,
} from 'lucide-react';
import { Language } from '@/types';
import { Link } from 'react-router-dom';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import EditorialHero from '@/components/ui/EditorialHero';

interface SafetyPageProps {
  lang: Language;
}

const FadeInWhenVisible: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const controls = useAnimation();
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] } }
      }}
    >
      {children}
    </motion.div>
  );
};

export const SafetyPage: React.FC<SafetyPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const isRtl = isAr;

  const t = {
    badge: isAr ? 'مركز الأمان والسيادة المكانية' : 'TRUST & SOVEREIGN SAFETY CENTER',
    heroTitle: isAr ? 'سيادة البيانات. أمان بنيوي حتمي.' : 'Sovereign by Design. Deterministic by Law.',
    heroSubtitle: isAr
      ? 'تم تصميم فكسل كطبقة ذكاء مكاني سيادية للبيانات الوطنية والمؤسسية الحساسة. نضمن التحقق الحتمي من أنظمة الإسناد، ومطابقة الفيزياء الجيوديسية، وانعدام تسريب البيانات أو استخدامها لتدريب نماذج عامة.'
      : 'VEXL is engineered as a sovereign spatial intelligence layer for sensitive physical assets. We guarantee deterministic CRS verification, geodetic physics validation, and zero-egress data sovereignty.',

    phasesTitle: isAr ? 'مبادئ الأمان المعمارية الثلاثة' : 'The Three Architectural Safety Tenets',
    phases: [
      {
        id: 'crs',
        icon: Compass,
        code: 'TENET-01 // CRS',
        title: isAr ? 'التحقق الحتمي من نظم الإسناد (CRS)' : 'Deterministic CRS Verification',
        desc: isAr
          ? 'معالجة صارمة ومؤتمتة لأنظمة الإسناد المكاني (EPSG:4326، EPSG:3857، وإسقاطات UTM). تدقيق إزاحات المرجع الجيوديسي لمنع أي انزياح موضعي للحدود المكانية.'
          : 'Strict, automated verification of Coordinate Reference Systems across all ingestion and execution paths. Rigorous datum-shift auditing prevents positional boundary drift.'
      },
      {
        id: 'physics',
        icon: Binary,
        code: 'TENET-02 // PHYSICS',
        title: isAr ? 'مطابقة الفيزياء الجيوديسية والأطياف' : 'Geodetic Physics & Spectral Audit',
        desc: isAr
          ? 'تطبيق حسابات كارني الجيوديسية الإهليلجية الدقيقة (WGS84). تدقيق حسابات الأطياف الفضائية (NDVI، NDWI) وفق فيزياء الانعكاس الإشعاعي دون أي هلوسة احتمالية.'
          : 'Enforcing Karney ellipsoidal geodesics and exact physical conservation laws. Satellite band indices (NDVI, NDWI) are calculated from calibrated radiometry, never guessed.'
      },
      {
        id: 'sovereignty',
        icon: ShieldCheck,
        code: 'TENET-03 // SOVEREIGNTY',
        title: isAr ? 'السيادة المكانية وانعدام التسريب' : 'Zero-Egress Data Sovereignty',
        desc: isAr
          ? 'نشر معزول شبكياً بالكامل (Air-Gapped) في مراكز البيانات الوطنية. لا تخرج أي بيانات أو إحداثيات خارج بيئة العميل، مع ضمانات قانونية بعدم تدريب أي نماذج عامة عليها.'
          : 'Full air-gapped readiness within sovereign national enclaves. Zero customer coordinates or telemetry ever egress, backed by strict legal non-training guarantees.'
      }
    ],

    cycleHeader: isAr ? 'معمارية التحقق المكاني المتكاملة' : 'VERIFIABLE SPATIAL PIPELINE',
    cycleTitle: isAr ? 'دورة حياة الأمان والاستدلال المكاني' : 'Spatial Intelligence Verification Lifecycle',
    cycleSubtitle: isAr
      ? 'نحن لا نبسط التعقيد، بل نجعله قابلاً للتحقق الصارم عبر بوابات تدقيق هندسية وحوكمة بيانات غير قابلة للاختراق.'
      : 'We do not simplify spatial complexity. We make it deterministically auditable through formal geometric gates and tamper-evident lineage.',

    pipelineStages: [
      {
        stage: '01',
        name: isAr ? 'التدقيق الإسنادي' : 'INGESTION & CRS PROVENANCE',
        desc: isAr
          ? 'التحقق التلقائي من نظام الإسناد، ومطابقة المسقط مع UTM Zone، واختبار الانغلاق الطوبولوجي للمضلعات.'
          : 'Automated EPSG datum auditing, UTM zone validation, and planar boundary topology invariant testing.',
        metrics: 'EPSG Invariant: 100% // Drift: 0.000m'
      },
      {
        stage: '02',
        name: isAr ? 'الاستدلال المقيد فيزيائياً' : 'PHYSICALLY BOUNDED INFERENCE',
        desc: isAr
          ? 'استخلاص مؤشرات الأقمار الصناعية ومعايرة انعكاس قمة الغلاف الجوي (TOA) وتطبيق خوارزميات الجيوديسيا الإهليلجية.'
          : 'Calibrated radiometric tensor reduction, top-of-atmosphere corrections, and Karney geodesic integration.',
        metrics: 'Sub-pixel accuracy // Solar zenith calibrated'
      },
      {
        stage: '03',
        name: isAr ? 'التنفيذ المعزول والتوثيق' : 'AIR-GAPPED AUDITED DISPATCH',
        desc: isAr
          ? 'توليد ملفات مكانية ذات تجزئة مشفرة (Cryptographic Hash) مع بقاء البيانات معزولة شبكياً بالكامل.'
          : 'Cryptographically hashed GeoJSON/GeoTIFF generation with zero telemetry egress and full air-gap custody.',
        metrics: 'Air-Gapped: Verified // Telemetry: 0 B/s'
      }
    ],

    focusTitle: isAr ? 'ركائز البنية التحتية للأمان' : 'Spatial Inference Security Infrastructure',
    focusSubtitle: isAr
      ? 'مصمم خصيصاً للمؤسسات والحكومات التي تُقاس قراراتها بالكيلومترات، وليس بالنقرات.'
      : 'Engineered for sovereign organizations whose operational decisions are measured in kilometers, not clicks.',

    focusAreas: [
      {
        icon: Lock,
        title: isAr ? 'النشر المعزول (Air-Gapped)' : 'Air-Gapped Enclave Deployment',
        desc: isAr ? 'تشغيل ذاتي مستقل بالكامل داخل مراكز بيانات العميل المغلقة.' : 'Self-contained offline execution in private sovereign enclaves.'
      },
      {
        icon: Eye,
        title: isAr ? 'تتبع سلالة البيانات الحسابية' : 'Cryptographic Data Lineage',
        desc: isAr ? 'إثبات رياضي موثق لكل بكسل ومضلع مستنتج.' : 'Mathematical audit provenance tracing outputs back to raw sensors.'
      },
      {
        icon: Shield,
        title: isAr ? 'ضوابط SOC 2 و ISO 27001' : 'SOC 2 & ISO 27001 Controls',
        desc: isAr ? 'حوكمة صارمة لأمن المعلومات وإدارة المخاطر المكانية.' : 'Rigorous information security controls and spatial risk management.'
      },
      {
        icon: Database,
        title: isAr ? 'عزل تام للطبقات والمضلعات' : 'Isolated Vector Sandboxing',
        desc: isAr ? 'عزل كامل لمضلعات الحقول والحيازات العقارية في الذاكرة.' : 'Strict memory containment for parcels and proprietary layers.'
      },
      {
        icon: Network,
        title: isAr ? 'استدلال موزع منخفض الكمون' : 'Distributed Geoprocessing Runtime',
        desc: isAr ? 'محركات استدلال مكانية موزعة بزمن استجابة أقل من ثانية.' : 'High-throughput C++/Rust spatial kernels operating at petabyte scale.'
      },
      {
        icon: FileText,
        title: isAr ? 'اختبارات الاختراق والفرق الحمراء' : 'Continuous Red Teaming & Audit',
        desc: isAr ? 'تقييمات دورية لمقاومة هجمات حقن الأوامر المكانية.' : 'Proactive testing against spatial prompt injection and adversarial vectors.'
      },
    ],

    learnMoreHeader: isAr ? 'الأوراق البحثية والتوثيق الأمني' : 'RESEARCH & TECHNICAL MEMOS',
    learnMore: isAr ? 'وثائق وبحوث الأمان المكاني' : 'Spatial Safety Research & Whitepapers',
    articles: [
      {
        title: isAr ? 'الامتثال التنظيمي للذكاء الاصطناعي الجغرافي' : 'GeoAI Regulatory Compliance & Sovereignty',
        desc: isAr ? 'إطار العمل القانوني للبيانات الجيومكانية الحساسة' : 'Legal frameworks for sensitive geospatial asset containment',
        code: 'RESEARCH MEMO // REG-01',
        tag: 'SOVEREIGNTY'
      },
      {
        title: isAr ? 'معمارية التحقق الحتمي في VEXL Nova' : 'Deterministic Verification in VEXL Nova',
        desc: isAr ? 'منع الهلوسة المكانية في النماذج المساحية الكبرى' : 'Eliminating spatial hallucinations in Large Spatial Models (LSMs)',
        code: 'RESEARCH MEMO // ENG-04',
        tag: 'GEODETICS'
      },
      {
        title: isAr ? 'معايرة الأطياف ومنع الانحياز الإشعاعي' : 'Mitigating Radiometric Bias in EO Pipelines',
        desc: isAr ? 'المعايرة الفيزيائية لمصفوفات Sentinel و Landsat' : 'Physical atmospheric calibration across Sentinel & Landsat arrays',
        code: 'RESEARCH MEMO // RS-02',
        tag: 'RADIOMETRY'
      },
      {
        title: isAr ? 'حوكمة البيانات المكانية الحكومية' : 'National Spatial Data Governance Architecture',
        desc: isAr ? 'بروتوكولات حماية الأصول الزراعية والمائية الوطنية' : 'Protocols for protecting sovereign agricultural & hydrological assets',
        code: 'RESEARCH MEMO // GOV-03',
        tag: 'GOVERNANCE'
      }
    ],

    ctaEyebrow: isAr ? 'نشر البنية المكانية السيادية' : 'SOVEREIGN DEPLOYMENT',
    ctaTitle: isAr ? 'امتلك منظومة الاستدلال المكاني بالكامل' : 'Own your spatial intelligence stack',
    ctaDesc: isAr
      ? 'انشر VEXL في بنيتك التحتية الخاصة أو بيئتك السحابية المعزولة مع حوكمة كاملة للبيانات.'
      : 'Deploy VEXL entirely within your private infrastructure or air-gapped enclave with complete sovereign governance.',
    ctaBtnPrimary: isAr ? 'استعراض التوثيق الفني' : 'Read Technical Documentation',
    ctaBtnSecondary: isAr ? 'التواصل مع فريق الأمان' : 'Contact Security Team',
  };

  return (
    <div
      className={`min-h-screen bg-[#05070A] text-[#EDF0F1] pt-20 md:pt-24 pb-20 ${
        isAr ? 'font-arabic' : 'font-sans'
      } relative overflow-hidden`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <SEO
        title={isAr ? 'مركز الأمان والسيادة المكانية | VEXL' : 'Trust & Sovereign Safety Center | VEXL'}
        description={t.heroSubtitle}
        lang={lang}
      />
      <RasterGridBackdrop type="type-a" />

      {/* ─── HERO SECTION ─── */}
      <div className="max-w-[1400px] mx-auto px-6 mb-16 relative z-10">
        <EditorialHero
          title={t.heroTitle}
          description={t.heroSubtitle}
          actions={[
            { label: isAr ? 'معمارية الأمان' : 'Security Architecture', href: '/security', variant: 'secondary' },
            { label: isAr ? 'الملكية الفكرية (EGIPA)' : 'IP Rights (EGIPA)', href: '/intellectual-property', variant: 'secondary' }
          ]}
          isAr={isAr}
        />
      </div>

      {/* ─── PHASES: 3 CORE ARCHITECTURAL SAFETY TENETS ─── */}
      <section className="max-w-[1400px] mx-auto px-6 mb-28 relative z-10">
        <div className="space-y-3 mb-8">
          <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
            {t.phasesTitle}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.phases.map((phase, idx) => (
            <FadeInWhenVisible key={phase.id} delay={idx * 0.1}>
              <div className="p-6 md:p-8 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all duration-200 h-full flex flex-col justify-between group shadow-sm">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#05070A] border border-[#262C31] flex items-center justify-center text-[#0E93A3] group-hover:border-[#0E93A3]/40 group-hover:bg-[#0E93A3]/10 transition-colors">
                      <phase.icon size={20} />
                    </div>
                    <span className="text-[10px] font-mono text-[#66707A] group-hover:text-[#9AA5AC] transition-colors tnum">
                      {phase.code}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors">
                    {phase.title}
                  </h3>

                  <p className="text-[#9AA5AC] text-xs md:text-sm leading-relaxed">
                    {phase.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-[#262C31] flex items-center gap-2 mt-6">
                  <CheckCircle2 size={13} className="text-[#0E93A3]" />
                  <span className="text-[10px] font-mono text-[#0E93A3] uppercase tracking-wider">
                    VERIFIED INVARIANT
                  </span>
                </div>
              </div>
            </FadeInWhenVisible>
          ))}
        </div>
      </section>

      {/* ─── VERIFIABLE SPATIAL PIPELINE (CLEAN GEODETIC SCHEMATIC) ─── */}
      <section className="max-w-[1400px] mx-auto px-6 mb-28 relative z-10">
        <FadeInWhenVisible>
          <div className="p-8 md:p-12 rounded-xl bg-[#12161A] border border-[#262C31] space-y-8">
            <div className="max-w-2xl space-y-2">
              <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
                {t.cycleHeader}
              </span>
              <h2
                className="text-2xl md:text-3xl font-medium text-[#EDF0F1] tracking-[-0.4px]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {t.cycleTitle}
              </h2>
              <p className="text-[#9AA5AC] text-sm leading-relaxed">
                {t.cycleSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
              {t.pipelineStages.map((stage, sIdx) => (
                <div
                  key={sIdx}
                  className="p-6 rounded-lg bg-[#05070A] border border-[#262C31] hover:border-[#0E93A3]/40 transition-colors space-y-4"
                >
                  <div className="flex items-center justify-between border-b border-[#262C31] pb-3">
                    <span className="text-xl font-mono font-bold text-[#0E93A3] tnum">{stage.stage}</span>
                    <span className="text-[9px] font-mono text-[#66707A] uppercase">STAGE PIPELINE</span>
                  </div>
                  <h3 className="text-sm font-semibold text-[#EDF0F1] tracking-wide">
                    {stage.name}
                  </h3>
                  <p className="text-xs text-[#9AA5AC] leading-relaxed">
                    {stage.desc}
                  </p>
                  <div className="pt-2 border-t border-[#262C31] text-[10px] font-mono text-[#0E93A3] tnum">
                    {stage.metrics}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FadeInWhenVisible>
      </section>

      {/* ─── PROTECTING WHERE IT MATTERS (FOCUS AREAS) ─── */}
      <section className="max-w-[1400px] mx-auto px-6 mb-28 relative z-10">
        <FadeInWhenVisible>
          <div className="space-y-3 mb-12">
            <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
              INFRASTRUCTURE HARDENING
            </span>
            <h2
              className="text-2xl md:text-3xl font-medium text-[#EDF0F1] tracking-[-0.4px]"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.focusTitle}
            </h2>
            <p className="text-[#9AA5AC] text-sm max-w-2xl leading-relaxed">
              {t.focusSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.focusAreas.map((area, idx) => (
              <div
                key={idx}
                className="p-6 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all duration-200 group shadow-sm space-y-3"
              >
                <div className="w-10 h-10 rounded-lg bg-[#05070A] border border-[#262C31] flex items-center justify-center text-[#0E93A3] group-hover:border-[#0E93A3]/40 group-hover:bg-[#0E93A3]/10 transition-colors">
                  <area.icon size={18} />
                </div>
                <h3 className="text-base font-semibold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors flex items-center justify-between">
                  <span>{area.title}</span>
                  <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#0E93A3]" />
                </h3>
                <p className="text-xs text-[#9AA5AC] leading-relaxed">
                  {area.desc}
                </p>
              </div>
            ))}
          </div>
        </FadeInWhenVisible>
      </section>

      {/* ─── RESEARCH & REPORTS (NO RAINBOW GRADIENTS) ─── */}
      <section className="max-w-[1400px] mx-auto px-6 mb-28 relative z-10">
        <FadeInWhenVisible>
          <div className="space-y-3 mb-10">
            <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
              {t.learnMoreHeader}
            </span>
            <h2
              className="text-2xl md:text-3xl font-medium text-[#EDF0F1] tracking-[-0.4px]"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.learnMore}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.articles.map((article, idx) => (
              <Link
                to="/research/overview"
                key={idx}
                className="group p-6 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all duration-200 flex flex-col justify-between h-full space-y-6 shadow-sm"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-mono text-[#0E93A3] bg-[#0E93A3]/10 border border-[#0E93A3]/25 px-2 py-0.5 rounded uppercase">
                      {article.tag}
                    </span>
                    <span className="text-[10px] font-mono text-[#66707A] tnum">{article.code}</span>
                  </div>
                  <h3 className="font-semibold text-base text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-xs text-[#9AA5AC] leading-relaxed">
                    {article.desc}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-[#262C31]">
                  <span className="text-[11px] font-mono text-[#66707A] group-hover:text-[#9AA5AC] transition-colors">
                    READ MEMO
                  </span>
                  <ArrowRight size={14} className="text-[#66707A] group-hover:text-[#0E93A3] group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </FadeInWhenVisible>
      </section>

      {/* ─── CTA SECTION ─── */}
      <section className="max-w-[1400px] mx-auto px-6 mb-16 relative z-10">
        <FadeInWhenVisible>
          <div className="rounded-xl p-8 md:p-14 text-center border border-[#262C31] bg-[#12161A] relative overflow-hidden space-y-6">
            <span className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3] block">
              {t.ctaEyebrow}
            </span>
            <h2
              className="text-2xl md:text-4xl font-medium text-[#EDF0F1] max-w-2xl mx-auto leading-tight"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.ctaTitle}
            </h2>
            <p className="text-[#9AA5AC] text-sm md:text-base max-w-xl mx-auto leading-relaxed">
              {t.ctaDesc}
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap pt-2">
              <Link
                to="/research/index"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white text-xs font-semibold tracking-wide transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)]"
              >
                <span>{t.ctaBtnPrimary}</span>
                <ArrowRight size={14} className={isRtl ? 'rotate-180' : ''} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#05070A] border border-[#262C31] hover:border-[#0E93A3] text-[#EDF0F1] text-xs font-medium tracking-wide transition-all"
              >
                <span>{t.ctaBtnSecondary}</span>
              </Link>
            </div>
          </div>
        </FadeInWhenVisible>
      </section>
    </div>
  );
};

export default SafetyPage;
